/**
 * Security utilities for Smart Budget app
 * These functions help protect user data and prevent common security issues
 */

/**
 * Simple hash function for passwords using Web Crypto API
 * Note: For production, consider using a backend with proper bcrypt/argon2
 */
export async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(password)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Verify password against hash
 */
export async function verifyPassword(
  password: string,
  hash: string,
): Promise<boolean> {
  const passwordHash = await hashPassword(password)
  return passwordHash === hash
}

/**
 * Validate password strength
 * Requirements: min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long')
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number')
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Password must contain at least one special character')
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Sanitize user input to prevent XSS
 */
export function sanitizeInput(input: string): string {
  const div = document.createElement('div')
  div.textContent = input
  return div.innerHTML
}

/**
 * Rate limiting for login attempts
 */
class RateLimiter {
  private attempts: Map<string, { count: number; timestamp: number }> =
    new Map()
  private readonly maxAttempts = 5
  private readonly windowMs = 15 * 60 * 1000 // 15 minutes

  isBlocked(identifier: string): boolean {
    const record = this.attempts.get(identifier)
    if (!record) return false

    const now = Date.now()
    if (now - record.timestamp > this.windowMs) {
      this.attempts.delete(identifier)
      return false
    }

    return record.count >= this.maxAttempts
  }

  recordAttempt(identifier: string): void {
    const now = Date.now()
    const record = this.attempts.get(identifier)

    if (!record || now - record.timestamp > this.windowMs) {
      this.attempts.set(identifier, { count: 1, timestamp: now })
    } else {
      record.count++
      record.timestamp = now
    }
  }

  getRemainingAttempts(identifier: string): number {
    const record = this.attempts.get(identifier)
    if (!record) return this.maxAttempts

    const now = Date.now()
    if (now - record.timestamp > this.windowMs) {
      this.attempts.delete(identifier)
      return this.maxAttempts
    }

    return Math.max(0, this.maxAttempts - record.count)
  }

  getBlockedTimeRemaining(identifier: string): number {
    const record = this.attempts.get(identifier)
    if (!record) return 0

    const now = Date.now()
    const elapsed = now - record.timestamp
    return Math.max(0, this.windowMs - elapsed)
  }

  reset(identifier: string): void {
    this.attempts.delete(identifier)
  }
}

export const loginRateLimiter = new RateLimiter()

/**
 * Session timeout management
 */
export class SessionManager {
  private readonly timeoutMs = 30 * 60 * 1000 // 30 minutes
  private timeoutId: number | null = null
  private lastActivity: number = Date.now()

  startSession(onTimeout: () => void): void {
    this.lastActivity = Date.now()
    this.resetTimeout(onTimeout)

    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']
    activityEvents.forEach((event) => {
      document.addEventListener(event, () => this.updateActivity(onTimeout))
    })
  }

  private updateActivity(onTimeout: () => void): void {
    this.lastActivity = Date.now()
    this.resetTimeout(onTimeout)
  }

  private resetTimeout(onTimeout: () => void): void {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId)
    }
    this.timeoutId = window.setTimeout(() => {
      onTimeout()
    }, this.timeoutMs)
  }

  endSession(): void {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  getTimeRemaining(): number {
    const elapsed = Date.now() - this.lastActivity
    return Math.max(0, this.timeoutMs - elapsed)
  }
}

/**
 * Validate email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Generate secure random password
 */
export function generateSecurePassword(length: number = 12): string {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const numbers = '0123456789'
  const special = '!@#$%^&*()_+-=[]{}|'
  const all = uppercase + lowercase + numbers + special

  let password = ''

  // Ensure at least one of each type
  password += uppercase[Math.floor(Math.random() * uppercase.length)]
  password += lowercase[Math.floor(Math.random() * lowercase.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  password += special[Math.floor(Math.random() * special.length)]

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)]
  }

  // Shuffle the password
  return password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('')
}

/**
 * Biometric Authentication using Web Authentication API (WebAuthn)
 */

/**
 * Check if biometric authentication is supported
 */
export function isBiometricSupported(): boolean {
  return !!(
    window.PublicKeyCredential &&
    navigator.credentials &&
    navigator.credentials.create
  )
}

/**
 * Get friendly name for biometric type based on platform
 */
export function getBiometricName(): string {
  const userAgent = navigator.userAgent.toLowerCase()

  if (
    userAgent.includes('iphone') ||
    userAgent.includes('ipad') ||
    userAgent.includes('mac')
  ) {
    return 'Face ID / Touch ID'
  } else if (userAgent.includes('android')) {
    return 'Fingerprint / Face Unlock'
  } else if (userAgent.includes('windows')) {
    return 'Windows Hello'
  }

  return 'Biometric Login'
}

/**
 * Convert ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Convert Base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Register biometric credential for a user
 */
export async function registerBiometric(username: string): Promise<boolean> {
  if (!isBiometricSupported()) {
    throw new Error('Biometric authentication is not supported on this device')
  }

  try {
    // Generate a challenge (in production, this should come from server)
    const challenge = new Uint8Array(32)
    crypto.getRandomValues(challenge)

    // Create credential
    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          name: 'Smart Budget',
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(username),
          name: username,
          displayName: username,
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
        },
        timeout: 60000,
        attestation: 'none',
      },
    })) as PublicKeyCredential

    if (!credential) {
      return false
    }

    // Store credential ID
    const credentialId = arrayBufferToBase64(credential.rawId)
    const biometricData = {
      credentialId,
      username,
      createdAt: new Date().toISOString(),
    }

    localStorage.setItem(`biometric_${username}`, JSON.stringify(biometricData))
    return true
  } catch (error) {
    console.error('Biometric registration failed:', error)
    return false
  }
}

/**
 * Authenticate using biometric credential
 */
export async function authenticateBiometric(
  username: string,
): Promise<boolean> {
  if (!isBiometricSupported()) {
    throw new Error('Biometric authentication is not supported on this device')
  }

  try {
    // Get stored credential
    const stored = localStorage.getItem(`biometric_${username}`)
    if (!stored) {
      return false
    }

    const biometricData = JSON.parse(stored)
    const credentialId = base64ToArrayBuffer(biometricData.credentialId)

    // Generate challenge
    const challenge = new Uint8Array(32)
    crypto.getRandomValues(challenge)

    // Authenticate
    const assertion = (await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [
          {
            id: credentialId,
            type: 'public-key',
            transports: ['internal'],
          },
        ],
        userVerification: 'required',
        timeout: 60000,
      },
    })) as PublicKeyCredential

    return !!assertion
  } catch (error) {
    console.error('Biometric authentication failed:', error)
    return false
  }
}

/**
 * Check if user has biometric enabled
 */
export function hasBiometricEnabled(username: string): boolean {
  return !!localStorage.getItem(`biometric_${username}`)
}

/**
 * Remove biometric credential
 */
export function removeBiometric(username: string): void {
  localStorage.removeItem(`biometric_${username}`)
}

/**
 * Get last used username for biometric login
 */
export function getLastBiometricUsername(): string | null {
  return localStorage.getItem('last_biometric_username')
}

/**
 * Set last used username for biometric login
 */
export function setLastBiometricUsername(username: string): void {
  localStorage.setItem('last_biometric_username', username)
}
